import { Fragment, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'

import { DrawerBodyWrapper, DrawerFooterWrapper, Tabs, TOAST_TYPE } from '@base'
import { Button } from '@baseV2'
import { ButtonsWrapper } from '@pages/Admin/Enquiry/ManageEnquiries/ManageEnquiry/EditSupplierQuotes/styles'
import {
	BodyCell,
	CellInput,
	CellSelect,
	DeleteButton,
	EmptyState,
	HeaderCell,
	HelperText,
	MethodChip,
	RowActions,
	TermsGrid,
	TypeCellContent,
} from '@pages/Admin/Enquiry/ManageEnquiries/ManageEnquiry/PaymentTerms/stylesV2'
import { strings, buyerTermsTypes, supplierTermsTypes, buyerConditionsByTerm, supplierConditionsByTerm, paymentTermTypeOptions } from '@constants'
import { Spacings } from '@styles'
import { Delete } from '@material-ui/icons'
import { isBuyer as checkBuyer, isSupplier as checkSupplier } from '@utils/adminFilter'
import { isPositiveIntegerString, preventNonIntegerKeyDown } from '@utils'
import { showToast } from '@data/state/action'

const TabsContainer = styled.div`
	padding: ${Spacings.SPACING_2B} 0 ${Spacings.SPACING_4B};
`

const TABS = {
	BUYER: 'BUYER',
	SUPPLIER: 'SUPPLIER',
}

// canonical section order — rows are always grouped/inserted in this sequence
const SECTION_ORDER = ['ADVANCE', 'BG', 'CREDIT', 'LC']
const sectionIndex = (paymentTerm) => {
	const idx = SECTION_ORDER.indexOf(paymentTerm)
	return idx === -1 ? SECTION_ORDER.length : idx // unknown types sit at the end
}

// a row only counts once it carries a percentage; rows without one are dropped on submit, so
// clearing the percentage is how an existing term is removed (parent rows have no delete button)
const isActiveRow = (row) => row?.percentage !== '' && Number(row?.percentage) > 0

// required for every active row
const INTERDEPENDENT_FIELDS = ['tenure_in_days', 'percentage', 'condition']

const OrgPaymentTerms = ({ edit = false, buyerData, supplierData, callbackFunction, organisation, viewOnly }) => {
	const dispatch = useDispatch()

	const isBuyer = checkBuyer(organisation?.roles)
	const isLogisticsSupplier = (organisation?.roles || []).some((item) => item.role === 'SUPPLIER' && item.platform === 'LOGISTICS' && item.approved)
	const isSupplier = checkSupplier(organisation?.roles) || isLogisticsSupplier
	const availableTabs = [isBuyer && TABS.BUYER, isSupplier && TABS.SUPPLIER].filter(Boolean)

	const [activeTab, setActiveTab] = useState(availableTabs[0])
	const isSupplierTab = activeTab === TABS.SUPPLIER

	// unique, stable id per row so multiple rows of the same payment_term coexist — shared by both tabs
	const idRef = useRef(0)
	const nextId = () => (idRef.current += 1)

	// one row per existing entry, preserving saved order and duplicates, then ensure every
	// available payment term type is visible with at least one row, grouped into section order
	const deriveRows = (data, termOptions) => {
		const dataRows = (data ?? []).map((item) => ({
			id: nextId(),
			payment_term: item?.payment_term,
			label: termOptions.find((_term) => _term.key === item?.payment_term)?.label ?? item?.payment_term,
			type: item?.type ?? 'IFC',
			tenure_in_days: item?.tenure_in_days ?? '',
			percentage: item?.percentage ?? '',
			condition: item?.condition ?? '',
		}))
		const missingRows = termOptions
			.filter((_term) => !dataRows.some((row) => row.payment_term === _term.key))
			.map((_term) => ({ id: nextId(), payment_term: _term.key, label: _term.label, type: 'IFC', tenure_in_days: '', percentage: '', condition: '' }))
		const allRows = [...dataRows, ...missingRows]
		return allRows.slice().sort((a, b) => sectionIndex(a.payment_term) - sectionIndex(b.payment_term))
	}

	// ─── Buyer / supplier payment term rows — both kept alive regardless of active tab ────────
	const [buyerRows, setBuyerRows] = useState(() => deriveRows(buyerData, buyerTermsTypes))
	const [supplierRows, setSupplierRows] = useState(() => deriveRows(supplierData, supplierTermsTypes))

	const rows = isSupplierTab ? supplierRows : buyerRows
	const setRows = isSupplierTab ? setSupplierRows : setBuyerRows
	const conditionsByTerm = isSupplierTab ? supplierConditionsByTerm : buyerConditionsByTerm

	const tabsData = [
		{
			label: `${strings('buyer')}`,
			selected: activeTab === TABS.BUYER,
			onClick: () => setActiveTab(TABS.BUYER),
			hide: !availableTabs.includes(TABS.BUYER),
		},
		{
			label: `${strings('supplier')}`,
			selected: activeTab === TABS.SUPPLIER,
			onClick: () => setActiveTab(TABS.SUPPLIER),
			hide: !availableTabs.includes(TABS.SUPPLIER),
		},
	]

	const updateRow = (id, field) => (value) => {
		setRows((_rows) =>
			_rows.map((row) => {
				if (row.id !== id) return row
				const updated = { ...row, [field]: value, [`${field}Error`]: undefined }
				// percentage cleared — the row is on its way out, so drop any stale required-field errors
				return isActiveRow(updated)
					? updated
					: { ...updated, tenure_in_daysError: undefined, percentageError: undefined, conditionError: undefined }
			})
		)
	}

	// pasting a non-positive-integer into tenure/percentage is rejected outright and flags an inline error
	const handleIntegerPaste = (id, field) => (e) => {
		const pasted = e.clipboardData.getData('text').trim()
		if (isPositiveIntegerString(pasted)) return
		e.preventDefault()
		setRows((_rows) => _rows.map((row) => (row.id === id ? { ...row, [`${field}Error`]: strings('must_be_positive_whole_number') } : row)))
	}

	// insert a new row of the chosen type into its section: after the last row of the same
	// (or earlier) section, before the first row of a later section — appended if none exists
	const addRow = (_term) => {
		const newRow = { id: nextId(), payment_term: _term.key, label: _term.label, type: 'IFC', tenure_in_days: '', percentage: '', condition: '' }
		setRows((_rows) => {
			const target = sectionIndex(_term.key)
			const insertAt = _rows.findIndex((row) => sectionIndex(row.payment_term) > target)
			return insertAt === -1 ? [..._rows, newRow] : [..._rows.slice(0, insertAt), newRow, ..._rows.slice(insertAt)]
		})
	}

	const deleteRow = (id) => {
		setRows((_rows) => _rows.filter((row) => row.id !== id))
	}

	const rowsToTermList = (_rows) =>
		_rows
			.filter(isActiveRow)
			.map((row) => ({
				payment_term: row.payment_term,
				...(row.payment_term === 'LC' ? { type: row.type } : {}),
				tenure_in_days: Number(row.tenure_in_days),
				condition: row.condition,
				percentage: Number(row.percentage),
			}))

	// tenure/condition are required only on rows that carry a percentage; the rest are ignored
	const validateRows = (rowsToValidate) => {
		let hasError = false
		const validatedRows = rowsToValidate.map((row) => {
			if (!isActiveRow(row)) {
				return { ...row, tenure_in_daysError: undefined, percentageError: undefined, conditionError: undefined }
			}
			const rowErrors = {}
			INTERDEPENDENT_FIELDS.forEach((field) => {
				if (row[field] === '') {
					rowErrors[`${field}Error`] = strings(field, 'required')
					hasError = true
				} else {
					rowErrors[`${field}Error`] = undefined
				}
			})
			return { ...row, ...rowErrors }
		})
		return { validatedRows, hasError }
	}

	const validateAndSubmit = () => {
		const { validatedRows: validatedBuyerRows, hasError: buyerRowError } = validateRows(buyerRows)
		const { validatedRows: validatedSupplierRows, hasError: supplierRowError } = validateRows(supplierRows)
		if (buyerRowError || supplierRowError) {
			setBuyerRows(validatedBuyerRows)
			setSupplierRows(validatedSupplierRows)
			return
		}

		const buyerTerms = rowsToTermList(validatedBuyerRows)
		const supplierTerms = rowsToTermList(validatedSupplierRows)

		const buyerTotal = buyerTerms.reduce((acc, item) => acc + item.percentage, 0)
		const supplierTotal = supplierTerms.reduce((acc, item) => acc + item.percentage, 0)

		if (buyerTerms.length > 0 && buyerTotal !== 100) {
			dispatch(showToast(true, 'Buyer payment terms should be 100 percentage!', { type: TOAST_TYPE.ERROR }))
			return
		}
		if (supplierTerms.length > 0 && supplierTotal !== 100) {
			dispatch(showToast(true, 'Supplier payment terms should be 100 percentage!', { type: TOAST_TYPE.ERROR }))
			return
		}
		callbackFunction({ buyerTerms, supplierTerms })
	}

	// keep Submit/Update inactive while any row, in either tab, is currently showing an error
	const rowsHaveError = [...buyerRows, ...supplierRows].some((row) => row.tenure_in_daysError || row.percentageError || row.conditionError)

	return (
		<>
			<DrawerBodyWrapper>
				<TabsContainer>
					<Tabs data={tabsData} />
				</TabsContainer>

				<TermsGrid>
					<HeaderCell>{strings('method')}</HeaderCell>
					<HeaderCell>{strings('tenure_in_days')}</HeaderCell>
					<HeaderCell>{strings('percentage')}</HeaderCell>
					<HeaderCell>{strings('condition')}</HeaderCell>
					<HeaderCell>{strings('action')}</HeaderCell>
					{rows.map((row, index) => {
						const conditionOptions = conditionsByTerm[row.payment_term] ?? []
						// the first row of each type is the parent (can add more rows); the rest are deletable children
						const isParent = rows.findIndex((r) => r.payment_term === row.payment_term) === index
						return (
							<Fragment key={row.id}>
								<BodyCell $noHPad>
									<TypeCellContent>
										<MethodChip>{row.label}</MethodChip>
										{!isSupplierTab && row.payment_term === 'LC' && (
											<CellSelect
												id={`type-${row.id}`}
												data={paymentTermTypeOptions}
												primaryKey='key'
												displayKey='label'
												disabled={viewOnly}
												value={paymentTermTypeOptions.find((option) => option.key === row.type) ?? null}
												onChange={(option) => updateRow(row.id, 'type')(option.key)}
											/>
										)}
									</TypeCellContent>
								</BodyCell>
								<BodyCell>
									<CellInput
										id={`tenure-${row.id}`}
										type='number'
										value={row.tenure_in_days}
										disabled={viewOnly}
										error={row.tenure_in_daysError}
										onKeyDown={preventNonIntegerKeyDown}
										onPaste={handleIntegerPaste(row.id, 'tenure_in_days')}
										onChange={(e) => updateRow(row.id, 'tenure_in_days')(e.target.value.replace(/[^0-9]/g, ''))}
									/>
								</BodyCell>
								<BodyCell>
									<CellInput
										id={`percentage-${row.id}`}
										type='number'
										value={row.percentage}
										disabled={viewOnly}
										error={row.percentageError}
										onKeyDown={preventNonIntegerKeyDown}
										onPaste={handleIntegerPaste(row.id, 'percentage')}
										onChange={(e) => updateRow(row.id, 'percentage')(e.target.value.replace(/[^0-9]/g, ''))}
									/>
								</BodyCell>
								<BodyCell>
									<CellSelect
										id={`condition-${row.id}`}
										placeholder={strings('select', 'condition')}
										data={conditionOptions}
										primaryKey='key'
										displayKey='label'
										disabled={viewOnly}
										error={row.conditionError}
										value={conditionOptions.find((option) => option.key === row.condition) ?? null}
										onChange={(option) => updateRow(row.id, 'condition')(option.key)}
									/>
								</BodyCell>
								<BodyCell>
									{!viewOnly && (
										<RowActions>
											{isParent ? (
												<Button
													variant='outline'
													color='neutral'
													size='small'
													onClick={() => addRow({ key: row.payment_term, label: row.label })}
												>
													{`+`}
												</Button>
											) : (
												<DeleteButton type='button' aria-label={strings('delete')} onClick={() => deleteRow(row.id)}>
													<Delete />
												</DeleteButton>
											)}
										</RowActions>
									)}
								</BodyCell>
							</Fragment>
						)
					})}
				</TermsGrid>
				{rows.length === 0 && <EmptyState>{strings('add_payment_term_to_begin')}</EmptyState>}
				<HelperText>{strings('payment_terms_total_100_helper')}</HelperText>
			</DrawerBodyWrapper>

			<DrawerFooterWrapper>
				<ButtonsWrapper>
					{!edit ? (
						<Button small onClick={validateAndSubmit} disabled={viewOnly || rowsHaveError}>
							{strings('submit')}
						</Button>
					) : (
						<Button small onClick={validateAndSubmit} disabled={viewOnly || rowsHaveError}>
							{strings('update')}
						</Button>
					)}
				</ButtonsWrapper>
			</DrawerFooterWrapper>
		</>
	)
}

export default OrgPaymentTerms
